import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, Info, TriangleAlert, Undo2, X } from 'lucide-react';
import { explorationApi } from './api';
import { buildIndex } from './model';
import NexusBot from './nexus/NexusBot';
import { useNexusProjectRefresh } from '../ProjectPage/tabs/shared';

// État partagé des onglets Attributs, Exploration et Artefacts : le graphe du projet, l'élément sélectionné, les
// écritures (qui renvoient le projet à jour), de petits messages avec « Annuler », et le copilote : ce que Nexus pense
// de chaque élément, ce qu'il fait en ce moment (lu toutes les secondes tant qu'il travaille).

const ExplorationContext = createContext(null);

/** Une réponse d'artefact proposée par Nexus et déjà notée (à la main, ou acceptée) : elle ne s'affiche plus. */
export const answerRecorded = (ix, item) => ix.resultsOf(item.artefact)
  .some((r) => r.candidate_id === item.candidate && r.lock_id === item.lock && (r.verdict || !item.verdict));

export const useExploration = () => useContext(ExplorationContext);

const REFRESH_MS = 90 * 1000;
const COPILOT_BUSY_MS = 600;        // Nexus relit ce qu'on vient d'écrire : on le voit écrire, proposition après proposition
const COPILOT_BACKGROUND_MS = 2500; // il relit le reste de la carte
const COPILOT_IDLE_MS = 8000;
const TOUCH_MS = 2500;
const COPILOT_TYPES = new Set(['lock', 'candidate', 'attribute', 'artefact']);
// Ce que Nexus relit (copilot.py, input_hash) : une modification d'autre chose ne le fait pas venir.
const COPILOT_PROPS = ['question', 'lock_type', 'threshold', 'threshold_text', 'stop_criterion', 'description', 'target', 'target_text',
  'no_locks_confirmed', 'exit', 'dismissed', 'objective', 'protocol', 'results', 'conclusion', 'measurements', 'status',
  'nature', 'level'];
const copilotWatches = (op) => op.op === 'update' && ('title' in op || Object.keys(op.props || {}).some((k) => COPILOT_PROPS.includes(k)));

/** Rythme de lecture de l'état du copilote : rapide quand il relit ce qu'on vient d'écrire. */
const copilotPace = (c) => {
  if (!c) return COPILOT_IDLE_MS;
  if (c.working?.interactive || c.queued > 0 || Object.values(c.nodes || {}).some((n) => n.status === 'pending')) return COPILOT_BUSY_MS;
  if (c.working || c.background > 0) return COPILOT_BACKGROUND_MS;
  return COPILOT_IDLE_MS;
};

export const ExplorationProvider = ({ projectId, employees, objectives, children, onStatus }) => {
  const [status, setStatus] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [highlight, setHighlight] = useState(null); // filtre de la barre « Demander » : {ids, explanation}
  const [artefactId, setArtefactId] = useState(null); // fiche d'artefact ouverte
  const [creator, setCreator] = useState(null); // nouvel artefact avec Nexus : {candidate_id?, lock_id?, prompt?}
  const [copilot, setCopilotState] = useState(null);
  const [touched, setTouched] = useState({}); // élément → instant où on vient de l'écrire (Nexus s'y pose aussitôt)
  const [draft, setDraft] = useState(null); // ce que Nexus dit pendant qu'on écrit un titre : {id, mood, text, tone}
  const statusRef = useRef(onStatus);
  statusRef.current = onStatus;
  const copilotKey = useRef('');
  const versionRef = useRef(0);
  const seenAuto = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const next = await explorationApi.status(projectId);
      setStatus(next);
      if (statusRef.current) statusRef.current(next);
      return next;
    } catch (err) {
      setStatus({ access: false, enabled: false });
      return null;
    }
  }, [projectId]);

  const reload = useCallback(async () => {
    try {
      const next = await explorationApi.bundle(projectId);
      setBundle(next);
      setError(null);
      return next;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [projectId]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { if (status?.enabled) reload(); }, [status?.enabled, reload]);
  useEffect(() => {
    if (!status?.enabled) return undefined;
    const timer = setInterval(() => { if (document.visibilityState === 'visible') reload(); }, REFRESH_MS);
    const onFocus = () => reload();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(timer); window.removeEventListener('focus', onFocus); };
  }, [status?.enabled, reload]);
  useNexusProjectRefresh(projectId, () => { if (status?.enabled) reload(); });

  const toast = useCallback((message, { tone = 'success', undo = null, ms = 5000, nexus = false } = {}) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((list) => [...list.slice(-3), { id, message, tone, undo, nexus }]);
    setTimeout(() => setToasts((list) => list.filter((t) => t.id !== id)), undo ? ms + 3000 : ms);
  }, []);

  // ── Copilote ──
  /** État du copilote, remplacé seulement s'il a changé (la carte ne se redessine pas pour rien). */
  const setCopilot = useCallback((next) => {
    if (!next || !next.nodes) return;
    const key = JSON.stringify(next);
    if (key === copilotKey.current) return;
    copilotKey.current = key;
    setCopilotState(next);
  }, []);

  useEffect(() => {
    if (!bundle?.copilot) return;
    versionRef.current = bundle.copilot.version || 0;
    setCopilot(bundle.copilot);
  }, [bundle, setCopilot]);

  const pace = copilotPace(copilot);
  useEffect(() => {
    if (!status?.enabled || !bundle || copilot?.enabled === false) return undefined;
    let alive = true;
    const tick = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const next = await explorationApi.copilot(projectId);
        if (!alive) return;
        setCopilot(next);
        if ((next.version || 0) > versionRef.current) { // Nexus (ou quelqu'un) a écrit : le graphe a changé
          versionRef.current = next.version;
          reload();
        }
      } catch { /* le copilote est un plus : on réessaiera */ }
    };
    const timer = setInterval(tick, pace);
    return () => { alive = false; clearInterval(timer); };
  }, [pace, status?.enabled, !!bundle, copilot?.enabled, projectId, reload, setCopilot]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Nexus s'y pose tout de suite, avant même la réponse du serveur. */
  const touch = useCallback((ids) => {
    const list = (ids || []).filter((id) => id != null);
    if (!list.length) return;
    const now = Date.now();
    setTouched((t) => { const next = { ...t }; list.forEach((id) => { next[id] = now; }); return next; });
    setTimeout(() => setTouched((t) => {
      const next = { ...t };
      list.forEach((id) => { if (next[id] === now) delete next[id]; });
      return next;
    }), TOUCH_MS + 100);
  }, []);

  const bundleRef = useRef(null);
  bundleRef.current = bundle;
  /** Applique des opérations ; le projet revient à jour. Renvoie le résultat (ou null si refusé, message affiché). */
  const apply = useCallback(async (operations, { motif, message, undo, quiet = false } = {}) => {
    if (!operations?.length) return null;
    setBusy(true);
    const kinds = new Map((bundleRef.current?.nodes || []).map((n) => [n.id, n.type]));
    touch(operations.filter((o) => copilotWatches(o) && COPILOT_TYPES.has(kinds.get(o.id))).map((o) => o.id));
    try {
      const response = await explorationApi.ops(projectId, operations, motif);
      if (response?.bundle) setBundle(response.bundle);
      if (!quiet) {
        const text = message || response?.result?.summaries?.[0];
        if (text) toast(text, { undo: undo ? () => apply(undo, { quiet: true }) : null });
      }
      return response?.result || null;
    } catch (err) {
      toast(err.message || 'Modification refusée', { tone: 'error', ms: 7000 });
      return null;
    } finally {
      setBusy(false);
    }
  }, [projectId, toast, touch]);

  /** Geste sur ce que Nexus propose : accept / dismiss / answer / undo / refresh. */
  const copilotAct = useCallback(async (action, nodeId, body = {}) => {
    if (action === 'refresh' || action === 'answer') touch([nodeId]);
    try {
      const response = await explorationApi.copilotAct(projectId, nodeId, action, body);
      if (response?.bundle) setBundle(response.bundle);
      else if (response?.nodes) setCopilot(response);
      if (response?.message) {
        const back = response.undo?.length ? response.undo : null;
        toast(response.message, { nexus: true, undo: back ? () => apply(back, { quiet: true }) : null });
      }
      return response || {};
    } catch (err) {
      toast(err.message || 'Nexus n’a pas pu le faire', { tone: 'error', ms: 7000 });
      return null;
    }
  }, [projectId, toast, apply, setCopilot, touch]);

  // Un lien que Nexus vient de faire seul : on le dit, avec « Annuler » (il ne le refera pas).
  useEffect(() => {
    if (!copilot?.nodes) return;
    const all = [];
    Object.entries(copilot.nodes).forEach(([id, n]) => (n.auto || []).forEach((a) => all.push({ ...a, node: Number(id), key: `${a.type}:${a.src}:${a.dst}:${a.at}` })));
    if (seenAuto.current === null) { seenAuto.current = new Set(all.map((a) => a.key)); return; }
    all.filter((a) => !seenAuto.current.has(a.key)).forEach((a) => {
      seenAuto.current.add(a.key);
      const why = a.by === 'kev+muse' ? 'Nexus sûr de lui' : a.probability ? `Nexus sûr à ${Math.round(a.probability * 100)} %` : '';
      toast(`Nexus a relié ${a.text}${why ? ` (${why})` : ''}`, {
        nexus: true, ms: 8000,
        undo: () => copilotAct('undo', a.node, { type: a.type, src: a.src, dst: a.dst }),
      });
    });
  }, [copilot, toast, copilotAct]);
  useEffect(() => { seenAuto.current = null; copilotKey.current = ''; }, [projectId]);

  /** État de Nexus sur un élément (avec ce qu'on vient d'écrire et qu'il n'a pas encore pris). */
  const copilotOf = useCallback((id) => {
    const state = copilot?.nodes?.[String(id)] || null;
    const at = touched[id];
    if (at && Date.now() - at < TOUCH_MS && !(state && (state.status === 'pending' || state.status === 'thinking'))) {
      const updated = state?.updated_at ? Date.parse(`${state.updated_at}Z`) : 0;
      if (!state || updated < at) return { ...(state || { items: [], auto: [] }), status: 'pending', step: null };
    }
    return state;
  }, [copilot, touched]);

  const ix = useMemo(() => (bundle ? buildIndex(bundle) : null), [bundle]);

  // Ce qu'un artefact semble trancher s'affiche aussi sur la carte du verrou : on l'accepte là où on travaille.
  const answers = useMemo(() => {
    const byLock = new Map();
    Object.entries(copilot?.nodes || {}).forEach(([id, n]) => (n.items || []).forEach((i) => {
      if (i.kind !== 'result' || (ix && answerRecorded(ix, i))) return;
      byLock.set(i.lock, [...(byLock.get(i.lock) || []), { ...i, owner: Number(id) }]);
    }));
    return byLock;
  }, [copilot, ix]);
  const copilotAnswers = useCallback((lockId) => answers.get(lockId) || [], [answers]);

  const enable = useCallback(async () => {
    await explorationApi.enable(projectId);
    const next = await loadStatus();
    if (next?.enabled) await reload();
  }, [projectId, loadStatus, reload]);

  /** Ouvre un élément : la fiche d'un artefact, le panneau de détail pour les autres. */
  const open = useCallback((id) => {
    const node = id != null ? bundle?.nodes?.find((n) => n.id === id) : null;
    if (node?.type === 'artefact') { setArtefactId(id); return; }
    setSelectedId(id);
  }, [bundle]);

  const value = useMemo(() => ({
    projectId, employees, objectives, status, bundle, ix, error, busy, reload, apply, toast, enable, loadStatus,
    selectedId, select: setSelectedId, open, artefactId, openArtefact: setArtefactId, highlight, setHighlight, setBundle,
    copilot, copilotOf, copilotAct, copilotAnswers, touch, draft, setDraft,
    creator, openCreator: (origin) => setCreator(origin || {}), closeCreator: () => setCreator(null),
  }), [projectId, employees, objectives, status, bundle, ix, error, busy, reload, apply, toast, enable, loadStatus,
    selectedId, open, artefactId, highlight, copilot, copilotOf, copilotAct, copilotAnswers, touch, draft, creator]);

  return (
    <ExplorationContext.Provider value={value}>
      {children}
      <Toasts toasts={toasts} onClose={(id) => setToasts((list) => list.filter((t) => t.id !== id))} />
    </ExplorationContext.Provider>
  );
};

const TOAST_ICON = { success: CheckCircle2, error: TriangleAlert, info: Info };
const TOAST_TONE = {
  success: 'border-emerald-200 text-emerald-900', error: 'border-rose-200 text-rose-900', info: 'border-slate-200 text-slate-800',
};

const Toasts = ({ toasts, onClose }) => (
  <div className="pointer-events-none fixed bottom-4 left-1/2 z-[80] flex w-[min(92vw,30rem)] -translate-x-1/2 flex-col gap-2">
    {toasts.map((t) => {
      const Icon = TOAST_ICON[t.tone] || Info;
      return (
        <div key={t.id} className={`pointer-events-auto flex items-start gap-2 rounded-xl border bg-white px-3 py-2 text-sm shadow-lg animate-muse-line-in ${t.nexus ? 'border-indigo-200 text-slate-800' : TOAST_TONE[t.tone] || TOAST_TONE.info}`}>
          {t.nexus ? <NexusBot mood={t.tone === 'error' ? 'error' : 'happy'} size={22} className="-my-0.5" /> : <Icon size={16} className="mt-0.5 shrink-0" />}
          <span className="min-w-0 flex-1">{t.message}</span>
          {t.undo && (
            <button type="button" onClick={() => { t.undo(); onClose(t.id); }}
              className="inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50">
              <Undo2 size={12} /> Annuler
            </button>
          )}
          <button type="button" onClick={() => onClose(t.id)} aria-label="Fermer" className="shrink-0 text-slate-400 hover:text-slate-700">
            <X size={14} />
          </button>
        </div>
      );
    })}
  </div>
);
